import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MasterDeviceAssignmentDto, BarrierDto } from '../../../../../api/gen';
import { MasterDeviceAssignmentService } from '../../../../../api/gen/api/masterDeviceAssignment.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-barrier-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule
  ],
  templateUrl: './create-barrier-form.component.html',
  styleUrl: './create-barrier-form.component.scss'
})
export class CreateBarrierFormComponent implements OnInit, OnChanges {
  @Input() isProcessing = false;
  @Input() parkingSpaceId: string | null = null;
  @Input() barrier: BarrierDto | null = null;
  @Input() mode: 'create' | 'edit' = 'create';
  @Output() createBarrier = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  barrierForm!: FormGroup;
  showAdvanced = signal(false);
  masterControllers = signal<MasterDeviceAssignmentDto[]>([]);
  isLoadingMasterControllers = signal(false);

  constructor(
    private fb: FormBuilder,
    private masterDeviceAssignmentService: MasterDeviceAssignmentService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadMasterControllers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['barrier'] && this.barrierForm) {
      if (this.barrier) {
        this.patchFormFromBarrier(this.barrier);
      } else {
        this.barrierForm.reset({
          deviceName: '',
          deviceDescription: '',
          deviceHardwareID: '',
          masterControllerId: null,
          config: {
            baseUrl: '',
            openPath: '',
            closePath: '',
            openPulsePath: '',
            toggleRelayPath: ''
          }
        });
      }
    }
  }

  private patchFormFromBarrier(barrier: BarrierDto): void {
    this.barrierForm.patchValue({
      deviceName: barrier.deviceName || '',
      deviceDescription: barrier.deviceDescription || '',
      deviceHardwareID: barrier.deviceHardwareID || '',
      masterControllerId: barrier.masterControllerId || null,
      config: {
        baseUrl: barrier.config?.baseUrl || '',
        openPath: barrier.config?.openPath || '',
        closePath: barrier.config?.closePath || '',
        openPulsePath: barrier.config?.openPulsePath || '',
        toggleRelayPath: barrier.config?.toggleRelayPath || ''
      }
    });
  }

  private initForm(): void {
    this.barrierForm = this.fb.group({
      deviceName: ['', [Validators.required, Validators.minLength(2)]],
      deviceDescription: [''],
      deviceHardwareID: [''],
      masterControllerId: [null],
      config: this.fb.group({
        baseUrl: ['', Validators.required],
        openPath: [''],
        closePath: [''],
        openPulsePath: [''],
        toggleRelayPath: ['']
      })
    });
  }

  async loadMasterControllers(): Promise<void> {
    if (!this.parkingSpaceId) {
      return;
    }

    this.isLoadingMasterControllers.set(true);
    try {
      const masterControllers = await firstValueFrom(
        this.masterDeviceAssignmentService.masterDeviceAssignmentsByParkingSpaceParkingSpaceIdGet(
          this.parkingSpaceId,
          false
        )
      );
      this.masterControllers.set(Array.isArray(masterControllers) ? masterControllers : []);
    } catch (error) {
      console.error('Error loading master controllers:', error);
      this.masterControllers.set([]);
    } finally {
      this.isLoadingMasterControllers.set(false);
    }
  }

  onSubmit(): void {
    if (this.barrierForm.valid) {
      const formData = this.barrierForm.getRawValue();
      if (!formData.deviceHardwareID) {
        const guid = this.generateGuidValue();
        this.barrierForm.patchValue({ deviceHardwareID: guid });
        formData.deviceHardwareID = guid;
      }
      this.createBarrier.emit(formData);
    }
  }

  onCancel(): void {
    this.barrierForm.reset();
    this.cancel.emit();
  }

  toggleAdvanced(): void {
    this.showAdvanced.update(v => !v);
  }

  generateGUID(): void {
    const guid = this.generateGuidValue();
    this.barrierForm.patchValue({ deviceHardwareID: guid });
  }

  private generateGuidValue(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getConfigError(fieldName: string): string {
    const control = this.barrierForm.get(`config.${fieldName}`);
    if (control?.hasError('required')) {
      return `${fieldName} is required`;
    }
    return '';
  }

}
